import L from "leaflet";
import type { TarkovMap } from "../maps";

/**
 * Reproduces the coordinate system tarkov.dev uses for its interactive maps, so
 * the artwork, its floor overlays and our pins all agree on where a point is.
 *
 * Map data stores bounds as [x, z] pairs; Leaflet treats them as latLng where
 * lat is the game's z axis and lng its x axis.
 */

function applyRotation(latLng: L.LatLng, rotation: number): L.LatLng {
  if (!rotation) return latLng;
  const radians = (rotation * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const { lng: x, lat: y } = latLng;
  return L.latLng(x * sin + y * cos, x * cos - y * sin);
}

export function createCrs(map: TarkovMap): L.CRS {
  const [scaleX, marginX, scaleZ, marginZ] = map.transform;
  const rotation = map.coordinateRotation;

  return L.extend({}, L.CRS.Simple, {
    transformation: new L.Transformation(scaleX, marginX, -scaleZ, marginZ),
    projection: L.extend({}, L.Projection.LonLat, {
      project: (latLng: L.LatLng) => L.Projection.LonLat.project(applyRotation(latLng, rotation)),
      unproject: (point: L.Point) => applyRotation(L.Projection.LonLat.unproject(point), -rotation),
    }),
  }) as L.CRS;
}

export function toLatLngBounds(bounds: [[number, number], [number, number]]): L.LatLngBounds {
  const [[x1, z1], [x2, z2]] = bounds;
  return L.latLngBounds([z1, x1], [z2, x2]);
}

/** Gives the view a little slack around the artwork so panning feels natural. */
export function paddedBounds(bounds: L.LatLngBounds, factor: number): L.LatLngBounds {
  const centre = bounds.getCenter();
  const halfLat = (Math.abs(bounds.getNorth() - bounds.getSouth()) / 2) * factor;
  const halfLng = (Math.abs(bounds.getEast() - bounds.getWest()) / 2) * factor;
  return L.latLngBounds(
    [centre.lat - halfLat, centre.lng - halfLng],
    [centre.lat + halfLat, centre.lng + halfLng],
  );
}
